<template>
  <material-card color="indigo" :title="$t('Pages.UserCheckoutForms.submit_suggestion')">
    <v-layout wrap align-start justify-start>
      <v-flex lg3>
        <v-text-field slot="actions-prepend"
          v-model="search.q"
          append-icon="search"
          :label="$t('Core.Fields.search')"
          single-line
          hide-details
          color="purple"
        ></v-text-field>
      </v-flex>
      <v-flex lg3 v-if="!hideSearch">
        <v-combobox
          v-model="search.statuses"
          :label="$t('Pages.UserCheckoutForms.model.status')"
          :items="statuses"
          single-line
          hide-details
          color="purple"
          multiple
        ></v-combobox>
      </v-flex>
      <v-flex lg6 v-if="!hideSearch">
        <v-btn round color="indigo" dark type="submit" @click="approveAll">
          {{$t('Pages.UserCheckoutForms.btn_submit')}}
        </v-btn>
        <v-btn round color="error" dark type="submit" @click="declineAll">
          {{$t('Pages.UserCheckoutForms.btn_decline')}}
        </v-btn>
        <v-btn round color="success" dark type="submit" @click="returnedAll">
          {{$t('Pages.UserCheckoutForms.btn_return')}}
        </v-btn>
      </v-flex>
    </v-layout>
    <v-layout wrap align-start justify-start v-if="!hideSearch">
      <v-flex>
        <v-expansion-panel>
          <v-expansion-panel-content>
            <template slot="header">
              <div class="title font-weight-regular">{{ $t('Core.Fields.advanced_search')}}</div>
            </template>
            <v-card>
              <v-card-text>
                <v-layout>
                  <v-flex lg3>
                    <v-dialog
                      ref="dialog"
                      v-model="modal"
                      :return-value.sync="search.start"
                      persistent
                      lazy
                      full-width
                      width="290px"
                    >
                      <v-text-field
                        slot="activator"
                        v-model="computedStart"
                        :label="$t('Pages.UserCheckoutForms.model.start')"
                        prepend-icon="event"
                        readonly
                        color="indigo"
                        clearable
                        name="start"
                        required
                      ></v-text-field>
                      <v-date-picker locale="sr-Latn-RS" first-day-of-week="1" color="indigo" v-model="search.start" scrollable>
                        <v-spacer></v-spacer>
                        <v-btn flat @click="modal = false">{{$t('Core.Fields.cancel')}}</v-btn>
                        <v-btn flat color="indigo" @click="$refs.dialog.save(search.start)">{{$t('Core.Fields.ok')}}</v-btn>
                      </v-date-picker>
                    </v-dialog>
                  </v-flex>
                  <v-flex lg3>
                    <v-datetime-picker
                      :label="$t('Pages.UserCheckoutForms.model.expiration')"
                      locale="sr-Latn-RS"
                      format="DD/MM/YYYY HH:mm:ss"
                      name="start"
                      prepend-icon="event"
                      v-model="search.end"/>
                  </v-flex>
                </v-layout>
                <v-layout>
                  <v-flex lg3>
                    <v-autocomplete
                      v-model="search.products"
                      :items="products"
                      :loading="productLoading"
                      name="products"
                      color="indigo"
                      hide-no-data
                      hide-selected
                      item-text="name"
                      :label="$t('Pages.Products.title_singular')"
                      :placeholder="$t('Common.type_to_search')"
                      prepend-icon="mdi-package-down"
                      return-object
                      multiple
                      clearable
                    ></v-autocomplete>
                  </v-flex>
                  <v-flex lg3>
                    <v-autocomplete
                      v-model="search.groupations"
                      :items="groupations"
                      :loading="groupationLoading"
                      name="groupations"
                      color="indigo"
                      hide-no-data
                      hide-selected
                      item-text="name"
                      :label="$t('Pages.Groupations.title_singular')"
                      :placeholder="$t('Common.type_to_search')"
                      prepend-icon="mdi-account-multiple"
                      return-object
                      multiple
                      clearable
                    ></v-autocomplete>
                  </v-flex>
                  <v-flex lg3>
                    <v-combobox
                      v-model="search.workplaces"
                      :label="$t('Pages.Workplaces.title_singular')"
                      :items="workplaces"
                      prepend-icon="mdi-briefcase"
                      item-text="name"
                      single-line
                      hide-details
                      color="purple"
                      multiple
                    ></v-combobox>
                  </v-flex>
                </v-layout>
              </v-card-text>
            </v-card>
          </v-expansion-panel-content>
        </v-expansion-panel>
      </v-flex>
    </v-layout>
    <v-data-table
      :headers="headers"
      :hide-actions="hideActions"
      :items="items"
      :pagination.sync="pagination"
      :no-data-text="$t('Common.no_data_text')"
      :rows-per-page-items="perPageItems"
      select-all
      :total-items="total"
      item-key="id"
    >
      <template slot="headers" slot-scope="{headers}">
        <tr>
          <th v-if="!hideSearch">
            <v-checkbox
              v-model="all"
              :indeterminate="indeterminate"
              primary
              hide-details
              @click.stop="toggleAll"
            ></v-checkbox>
          </th>
          <th
            v-for="header in headers"
            :key="header.text"
            :class="['column', header.sortable ? 'sortable' : '', pagination.descending ? 'desc' : 'asc', header.value === pagination.sortBy ? 'active' : '', header.align ? 'text-xs-' + header.align : 'text-xs-left']"
            @click="changeSort(header.value, header.sortable)"
          >
            <span class="subheading font-weight-light text-primary" v-text="header.text"/>
            <v-icon small v-if="header.sortable">arrow_upward</v-icon>
          </th>
        </tr>
      </template>
      <template slot="items" slot-scope="{item}">
        <tr>
          <td v-if="!hideSearch">
            <v-checkbox
              v-model="item.selected"
              primary
              hide-details
              :disabled="item.last_status === userCheckoutFormStatuses.EXPENDABLE"
              @click.stop="toggleSingle(item)"
            ></v-checkbox>
          </td>
          <td>{{ item.id }}</td>
          <td>{{ formatUsers(item.users) }}</td>
          <td align="center">{{ item.workplace.name }}</td>
          <td align="center"><a class="indigo--text font-weight-bold" target="_blank" :href="'/warehouses/' + item.warehouse.id + '/show'">{{ item.warehouse.name }}</a></td>
          <td align="center">{{ item.product ? item.product.name : item.groupation ? item.groupation.name : '' }}</td>
          <td>{{ getLocaleDateFormat(item.start) }}</td>
          <td :style="(new Date(item.expiration).getTime() - new Date().getTime()) < 604800000 ? 'color:red; font-weight: bold;' : ''">{{ getLocaleDateFormat(item.expiration, true) }}</td>
          <td style="font-weight: bold;" :style="item.last_status === 'izdato' ? 'color: #3f51b5;' : item.last_status === 'vraćeno' ? 'color: #5cb860;' : item.last_status === 'odbijeno' || item.last_status === 'oštećeno' ? 'color: #f55a4e;' : ''">{{ item.last_status }}</td>
          <td>{{ getLocaleDateFormat(item.updated_at, true) }}</td>
          <td class="text-xs-center">
            <v-tooltip top v-if="item.last_status === userCheckoutFormStatuses.EXPENDABLE">
              <v-btn slot="activator" class="mx-0" small flat icon color="tertiary lighten-2" @click="openReturnExpandableModal(item)">
                <v-icon small>mdi-restore</v-icon>
              </v-btn>
              <span>{{$t('Pages.UserCheckoutForms.btn_return_single')}}</span>
            </v-tooltip>

            <v-tooltip top v-if="!hideDuplicateBtn">
              <v-btn slot="activator" class="mx-0" small flat icon color="tertiary lighten-2" append :to="'/user-checkout-forms/add/' + item.id">
                <v-icon  small>mdi-plus-circle-multiple-outline</v-icon>
              </v-btn>
              <span>{{$t('Core.Fields.duplicate')}}</span>
            </v-tooltip>

            <v-tooltip top>
              <v-btn slot="activator" class="mx-0" small flat icon color="tertiary lighten-2" append :to="'/user-checkout-forms/' + item.id + '/show'">
                <v-icon  small>mdi-eye-settings</v-icon>
              </v-btn>
              <span>{{$t('Core.Fields.show')}}</span>
            </v-tooltip>

            <v-tooltip top v-if="!hideExportBtn">
              <v-btn slot="activator" class="mx-0" small flat icon color="success" append @click="openUserCheckoutFormStateModal(item)">
                <v-icon small>mdi-file-pdf</v-icon>
              </v-btn>
              <span>{{$t('Pages.Users.export_to_pdf')}}</span>
            </v-tooltip>

            <v-tooltip top v-if="!hideEditBtn">
              <v-btn slot="activator" class="mx-0" small flat icon color="warning" append :to="item.id + '/update'">
                <v-icon  small>edit</v-icon>
              </v-btn>
              <span>{{$t('Core.Fields.edit')}}</span>
            </v-tooltip>

            <v-tooltip top v-if="!hideDeleteBtn">
              <v-btn slot="activator" class="mx-0" small flat icon color="error" @click="openDeleteDialog(item)">
                <v-icon  small>delete</v-icon>
              </v-btn>
              <span>{{$t('Core.Fields.delete')}}</span>
            </v-tooltip>
          </td>
        </tr>
      </template>
    </v-data-table>
    <v-dialog v-model="deleteDialog" max-width="290">
      <v-card>
        <v-card-title class="headline">{{$t('Pages.Delete.title')}}</v-card-title>

        <v-card-text>
          {{$t('Pages.Delete.subtitle')}}
        </v-card-text>

        <v-card-actions>
          <v-spacer></v-spacer>

          <v-btn
            color="error darken-1"
            @click="deleteDialog = false; selectedItem = null"
          >
            {{$t('Common.no')}}
          </v-btn>

          <v-btn
            color="info darken-1"
            @click="deleteItem"
          >
            {{$t('Common.yes')}}
          </v-btn>
        </v-card-actions>
      </v-card>
  </v-dialog>
    <user-checkout-form-state-modal v-model="showStateModal" @close="showStateModal = false" :state="ucfStateInfo" :loading="exportingAgreement" @exportState="exportState" ></user-checkout-form-state-modal>
    <return-expandable-modal
      v-model="showReturnExpandableModal"
      :userCheckoutForm="selectedItem ? selectedItem : null"
      @close="showReturnExpandableModal = false; selectedItem = null;"
      @refresh="searchBySelections"
    ></return-expandable-modal>
  </material-card>
</template>

<script>
  import i18n from '@/i18n'
  import _ from 'lodash'
  import UserCheckoutFormStateModal from './UserCheckoutFormStateModal'
  import ReturnExpandableModal from './ReturnExpandableModal'
  export default {
    name: "SelectableTable",
    components: {UserCheckoutFormStateModal, ReturnExpandableModal},
    props: {
      items: {
        type: Array,
        required: true
      },
      selected: {
        type: Array,
        required: true,
        default: []
      },
      total: {
        type: Number,
        default: 0,
      },
      loading: {
        type: Boolean,
        required: true
      },
      hideActions: {
        type: Boolean,
        default: false
      },
      hideDuplicateBtn: {
        type: Boolean,
        default: false
      },
      hideExportBtn: {
        type: Boolean,
        default: false
      },
      hideEditBtn: {
        type: Boolean,
        default: false
      },
      hideDeleteBtn: {
        type: Boolean,
        default: false
      },
      hideSearch: {
        type: Boolean,
        default: false
      }
    },
    data: () => ({
      headers: [
        {
          sortable: false,
          text: '#',
          value: 'id'
        },
        {
          sortable: false,
          text: i18n.t('Pages.Users.title_singular'),
          value: 'users'
        },
        {
          sortable: false,
          text: i18n.t('Pages.Workplaces.title_singular'),
          value: 'workplace.name',
          align: 'center'
        },
        {
          sortable: false,
          text: i18n.t('Pages.Warehouses.title_singular'),
          value: 'warhouse.name',
          align: 'center'
        },
        {
          sortable: false,
          text: i18n.t('Pages.Products.title_singular') + '/' + i18n.t('Pages.Groupations.title_singular'),
          value: 'product' ? 'product.name' : 'groupation.name',
          align: 'center'
        },
        {
          sortable: false,
          text: i18n.t('Pages.UserCheckoutForms.model.start'),
          value: 'start'
        },
        {
          sortable: false,
          text: i18n.t('Pages.UserCheckoutForms.model.expiration'),
          value: 'expiration'
        },
        {
          sortable: false,
          text: i18n.t('Pages.UserCheckoutForms.model.status'),
          value: 'status'
        },
        {
          sortable: true,
          text: i18n.t('Pages.UserCheckoutForms.model.updated_at'),
          value: 'updated_at',
          align: 'center'
        },
        {
          sortable: false,
          text: i18n.t('Core.Fields.actions'),
          align: 'center'
        },
      ],
      pagination: {},
      all: false,
      search: {
        statuses: [],
        start: null,
        end: null,
        workplaces: null,
        products: null,
        groupations: null,
        q: '',
      },
      vehicles: [],
      modal: false,
      modalEnd: false,
      perPageItems: [10, 15, 25, 50, 100],
      statuses: [],
      searchData:{},
      products: [],
      productLoading: false,
      groupations: [],
      groupationLoading: false,
      workplaces: [],
      workplacesLoading: false,
      deleteDialog: false,
      selectedItem: null,
      showStateModal: false,
      ucfStateInfo: null,
      exportingAgreement: false,
      showReturnExpandableModal: false
    }),
    computed:{
      indeterminate() {
        return this.all ? false : ( (this.selected && this.selected.length >0  && this.selected.length !== this.items.length) ? true : false);
      },
      computedStart: {
        get() {
          return this.getLocaleDateFormat(this.search.start);
        },
        set(val) {
          return this.search.start = val
        }
      },
      computedEnd: {
        get() {
          return this.getLocaleDateFormat(this.search.end);
        },
        set(val) {
          return this.search.end = val
        }
      },
    },
    created(){
      this.statuses = [
        {
          value: this.userCheckoutFormStatuses.SUGGESTED,
          text: this.$t('Pages.UserCheckoutForms.status.suggested')
        },
        {
          value: this.userCheckoutFormStatuses.GIVEN,
          text: this.$t('Pages.UserCheckoutForms.status.given')
        },
        {
          value: this.userCheckoutFormStatuses.RETURNED,
          text: this.$t('Pages.UserCheckoutForms.status.returned')
        },
        {
          value: this.userCheckoutFormStatuses.BROKEN,
          text: this.$t('Pages.UserCheckoutForms.status.broken')
        },
        {
          value: this.userCheckoutFormStatuses.DENIED,
          text: this.$t('Pages.UserCheckoutForms.status.denied')
        },
      ];
      this.pagination.descending = true;
      if (!this.hideSearch) {
        this.getProducts();
        this.getGroupations();
        this.getWorkplaces();
      }
    },
    mounted(){

    },
    methods: {
      exportState(){
        this.exportingAgreement = true;

        this.repositories.userCheckoutForm.activeUserCheckoutFormsExport(this.selectedItem.id)
        .finally(() => {
          this.exportingAgreement = false;
        });
      },
      openUserCheckoutFormStateModal(item){
        this.selectedItem = item;
        this.ucfStateInfo = item.info;
        this.showStateModal = true;
      },
      searchBySelections(){
        let sort = 'id,desc';
        if (this.pagination.sortBy) {
          sort = this.pagination.sortBy + ',' + (this.pagination.descending ? 'desc' : 'asc')
        }
        let data = {
          q                   : this.search.q,
          statuses            : [],
          start               : this.search.start,
          end                 : this.search.end,
          workplaces          : [],
          products            : [],
          groupations         : [],
          page                : this.pagination.page,
          pagination          : this.pagination.rowsPerPage,
          orderBy             : sort
        }
        if(this.search.statuses) {
          this.search.statuses.forEach(element => {
            data.statuses.push(element.value)
          })
        }
        if(this.search.workplaces) {
          this.search.workplaces.forEach(element => {
            data.workplaces.push(element.id)
          })
        }
        if(this.search.products) {
          this.search.products.forEach(element => {
            data.products.push(element.id)
          })
        }
        if(this.search.groupations) {
          this.search.groupations.forEach(element => {
            data.groupations.push(element.id)
          })
        }
        if(this.search.end) {
          data.end = this.getLocalISOFormat(data.end);
        }

        this.$emit('searchBySelections', data);
        this.all = true;
        this.toggleAll();
      },
      toggleAll () {
        this.all = !this.all;
        for(var i = 0; i < this.items.length; i++) {
          let item = this.items[i];
          if(this.all) {
            if(!item.selected && item.last_status !== this.userCheckoutFormStatuses.EXPENDABLE) {
              this.selected.push(item);
            }
          }
          else{
            if(item.selected) {
              _.remove(this.selected, {
                id: item.id
              });
            }
          }
        }

        this.$emit('select', this.selected);
      },
      toggleSingle(item){
        if(item.selected) {
          _.remove(this.selected, {
            id: item.id
          });
        }
        else {
          this.selected.push(item);
        }
        this.all = this.selected.length === this.items.length;
        this.select(this.selected);
      },
      changeSort (column, sortable) {
        if(!sortable)return;
        if (this.pagination.sortBy === column) {
          this.pagination.descending = !this.pagination.descending;
        } else {
          this.pagination.sortBy = column;
          this.pagination.descending = false;
        }

        this.searchBySelections();
      },
      select(selected){
        this.$emit('select', selected);
      },
      async approveAll(){
        await this.$emit('approveAll', this.selected);
      },
      async declineAll(){
        await this.$emit('declineAll', this.selected);
      },
      async returnedAll(){
        await this.$emit('returnedAll', this.selected);
      },
      getProducts(){
        this.productLoading = true;
        this.repositories.product.all().then((response) => {
          this.products = response.data.data;
        }).finally(() => {
          this.productLoading = false;
        });
      },
      getGroupations(){
        this.groupationLoading = true;
        this.repositories.groupation.all().then((response) => {
          this.groupations = response.data.data;
        }).finally(() => {
          this.groupationLoading = false;
        });
      },
      getWorkplaces(){
        this.workplacesLoading = true;
        this.repositories.workplace.all().then((response) => {
          this.workplaces = response.data.data;
        }).finally(() => {
          this.workplacesLoading = false;
        });
      },
      openDeleteDialog(item) {
        this.deleteDialog = true
        this.selectedItem = item
      },
      deleteItem() {
        this.repositories.userCheckoutForm.delete(this.selectedItem.id).then((response) => {
          this.$toast.info(response.data.message);
          this.searchBySelections();
          this.deleteDialog = false
        })
      },
      formatUsers(users) {
        if (users && users.length > 0) {
          return users.map(user => user.profile.name).join(", ")
        } else return this.$t('Common.not_assigned')
      },
      openReturnExpandableModal(item) {
        this.selectedItem = item;
        this.showReturnExpandableModal = true;
      }
    },
    watch: {
      'search.q': _.debounce(function(){
        this.searchBySelections();
      }, 500),
      'search.statuses': function(){
        this.searchBySelections();
      },
      'search.start': function(){
        this.searchBySelections();
      },
      'search.end': function(){
        this.searchBySelections();
      },
      'search.products': function(){
        this.searchBySelections();
      },
      'search.groupations': function(){
        this.searchBySelections();
      },
      'search.workplaces': function(){
        this.searchBySelections();
      },
      pagination(){
        this.searchBySelections();
    },
  }
}
</script>

<style scoped>

</style>
